/**
 * A broker's stated figure beside the platform's own, and the variance (Gate 0 D1).
 *
 * Louis approved the conflict rule on 13 Oct 2026: a figure a broker states is
 * never overwritten or back-solved, the platform's calculation shows beside it,
 * and the gap is a variance, shown for information. The cap rate, value, NOI and
 * debt yield each have a broker-stated field now (the DSCR has its coverage
 * rows). The server calculates the comparison — `statedFigures` on the
 * field-lineage response, from the engine's own rules — and this only formats it:
 * there is no second copy of the arithmetic in the browser to drift from it.
 */

import type { StatedFigure, StatedFigureStatus } from '@/pages/pipeline-intelligence/types'
import { formatAmount, formatPoints } from './format'

export type { StatedFigure, StatedFigureStatus }

export type StatedFigureRow = {
  id: string
  field: string
  label: string
  stated: string
  /** The platform's own figure, or a dash where it cannot be calculated. */
  calculated: string
  /** `+0.50 pts (+8.3%)`; null where there is no variance to show. */
  variance: string | null
  /** The formula or, where there is no calculation, what it is waiting on. */
  basis: string
  status: StatedFigureStatus
}

const DASH = '—'

function sign(n: number): string {
  return n > 0 ? '+' : n < 0 ? '−' : ''
}

function show(value: number | null, unit: string | null): string {
  if (value === null || !Number.isFinite(value)) return DASH
  return unit === '%' ? formatPoints(value) : formatAmount(value)
}

/** The gap in the figure's own unit, with its size against the calculation. */
export function formatVariance(delta: number, deltaPct: number | null, unit: string | null): string {
  const size = unit === '%'
    ? `${Math.abs(delta).toFixed(2)} pts`
    : formatAmount(Math.abs(delta))
  const relative = deltaPct !== null && Number.isFinite(deltaPct)
    ? ` (${sign(deltaPct)}${Math.abs(deltaPct).toFixed(1)}%)`
    : ''
  return `${sign(delta)}${size}${relative}`
}

/**
 * The rows to show: only figures somebody stated. A figure nobody stated needs no
 * row — the base figure's own tile already says it is blank — and a block of
 * dashes is noise beside the coverage rows.
 */
export function buildStatedFigureRows(figures: readonly StatedFigure[] | null | undefined): StatedFigureRow[] {
  return (figures ?? [])
    .filter((figure) => figure.status !== 'not_stated' && figure.stated !== null)
    .map((figure) => {
      const variance = figure.status === 'variance' && figure.delta !== null
        ? formatVariance(figure.delta, figure.deltaPct, figure.unit)
        : null
      const calculated = show(figure.calculated, figure.unit)
      let basis: string
      if (figure.status === 'variance') {
        basis = `${figure.formula ?? 'The platform’s calculation'}. For information — nothing is chosen between the two.`
      } else if (figure.status === 'agrees') {
        basis = `Agrees with the platform’s calculation${figure.formula ? ` (${figure.formula})` : ''}.`
      } else {
        basis = figure.note ?? 'No platform calculation is available for this figure.'
      }
      return {
        id: figure.base,
        field: figure.field,
        label: figure.label,
        stated: show(figure.stated, figure.unit),
        calculated,
        variance,
        basis,
        status: figure.status,
      }
    })
}
